import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Document, Page, pdfjs } from 'react-pdf';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Input from '../../components/common/Input';
import Spinner from '../../components/common/Spinner';
import { useUIStore } from '../../stores/uiStore';
import * as contractTemplatesApi from '../../api/contractTemplates';

// Configure PDF.js worker - use local worker file bundled with Vite
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

/**
 * Signature Zone Editor
 * Visual editor for defining TRAINER signature placement zones on contract templates
 * NOTE: Creator signatures are now handled via {tc_user_signature} template variable
 */

/**
 * Signature Zone Editor
 * Visual editor for defining signature placement zones on contract templates
 */
export default function SignatureZoneEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useUIStore();
  const containerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [template, setTemplate] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);

  useEffect(() => {
    loadTemplate();
  }, [id]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const response = await contractTemplatesApi.getTemplate(id);
      // API returns data directly, not wrapped in .data
      const templateData = response.data || response;
      setTemplate(templateData);
      
      // Parse signature_zones if it's a JSON string (filter for trainer zones only)
      let zones = templateData.signature_zones || [];
      if (typeof zones === 'string') {
        try {
          zones = JSON.parse(zones);
        } catch (e) {
          console.warn('Failed to parse signature_zones:', e);
          zones = [];
        }
      }
      // Only keep trainer zones (creator zones are no longer used)
      const trainerZones = Array.isArray(zones) ? zones.filter(z => z.type === 'trainer') : [];
      setZones(trainerZones);
      
      // Auto-generate preview PDF
      await generatePreviewPdf(id);
    } catch (error) {
      showToast('Failed to load template', 'error');
      console.error('Error loading template:', error);
      navigate('/templates');
    } finally {
      setLoading(false);
    }
  };

  const generatePreviewPdf = async (templateId) => {
    try {
      setGeneratingPreview(true);
      const pdfBlob = await contractTemplatesApi.generatePreviewPdf(templateId);
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
    } catch (error) {
      showToast('Failed to generate PDF preview', 'error');
      console.error('Error generating preview PDF:', error);
    } finally {
      setGeneratingPreview(false);
    }
  };

  // Cleanup PDF URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    console.log('[SignatureZoneEditor] PDF loaded successfully with pages:', numPages);
    setNumPages(numPages);
  };
  
  const onDocumentLoadError = (error) => {
    console.error('[SignatureZoneEditor] PDF load error:', error);
    showToast('Failed to load PDF. Check console for details.', 'error');
  };

  const handleMouseDown = (e) => {
    if (e.target.classList.contains('pdf-page')) {
      const rect = e.target.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      
      setIsDragging(true);
      setDragStart({ x, y, page: currentPage });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !dragStart) return;

    const rect = e.target.closest('.pdf-page')?.getBoundingClientRect();
    if (!rect) return;

    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    // Create temporary zone for preview
    const tempZone = {
      id: 'temp',
      page: dragStart.page,
      x: Math.min(dragStart.x, x),
      y: Math.min(dragStart.y, y),
      width: Math.abs(x - dragStart.x),
      height: Math.abs(y - dragStart.y),
    };

    setSelectedZone(tempZone);
  };

  const handleMouseUp = (e) => {
    if (!isDragging || !dragStart) return;

    const rect = e.target.closest('.pdf-page')?.getBoundingClientRect();
    if (!rect) {
      setIsDragging(false);
      setDragStart(null);
      setSelectedZone(null);
      return;
    }

    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const width = Math.abs(x - dragStart.x);
    const height = Math.abs(y - dragStart.y);

    // Only create zone if it's large enough
    if (width > 2 && height > 2) {
      const newZone = {
        id: Date.now(),
        page: dragStart.page,
        x: Math.min(dragStart.x, x),
        y: Math.min(dragStart.y, y),
        width,
        height,
        type: 'trainer', // Only trainer zones allowed now
        label: 'Trainer Signature',
      };

      setZones([...zones, newZone]);
      setSelectedZone(newZone);
    }

    setIsDragging(false);
    setDragStart(null);
  };

  const handleDeleteZone = (zoneId) => {
    setZones(zones.filter(z => z.id !== zoneId));
    if (selectedZone?.id === zoneId) {
      setSelectedZone(null);
    }
  };

  const handleUpdateZone = (zoneId, updates) => {
    setZones(zones.map(z => z.id === zoneId ? { ...z, ...updates } : z));
    if (selectedZone?.id === zoneId) {
      setSelectedZone({ ...selectedZone, ...updates });
    }
  };

  const handleSave = async () => {
    // Variable mode doesn't require zones
    if (template?.signature_mode !== 'variable' && zones.length === 0) {
      showToast('Please define at least one signature zone', 'error');
      return;
    }

    try {
      setSaving(true);
      await contractTemplatesApi.updateTemplate(id, {
        signature_zones: zones,
      });
      showToast('Signature zones saved successfully', 'success');
      navigate('/templates');
    } catch (error) {
      showToast('Failed to save signature zones', 'error');
      console.error('Error saving zones:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="secondary"
          onClick={() => navigate('/templates')}
          className="flex-shrink-0"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">
            Define Signature Zones
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {template?.name}
          </p>
        </div>
        <Button onClick={handleSave} loading={saving}>
          Save Signature Zones
        </Button>
      </div>

      {/* Variable Mode Notice */}
      {template?.signature_mode === 'variable' && (
        <Card className="bg-green-50 border-green-200">
          <div className="text-sm text-green-800">
            <h4 className="font-semibold mb-2">✅ This template uses Variable-Based signatures</h4>
            <p className="mb-2">
              Trainer signatures are placed using the <code className="bg-green-200 px-1 rounded">{'{trainer_signature}'}</code> variable
              in your template HTML. This allows signatures to flow naturally with your content, solving positioning issues
              with dynamic payment schedules.
            </p>
            <p className="font-medium">
              ℹ️ Signature zones are not required for variable-based templates. The signature will appear wherever you
              placed the <code className="bg-green-200 px-1 rounded">{'{trainer_signature}'}</code> variable in your template.
            </p>
          </div>
        </Card>
      )}

      {/* Instructions */}
      {template?.signature_mode !== 'variable' && (
        <Card className="bg-blue-50 border-blue-200">
          <div className="text-sm text-blue-800">
            <h4 className="font-semibold mb-2">How to define trainer signature zones:</h4>
            <ol className="list-decimal list-inside space-y-1">
              <li>The PDF preview is automatically generated from your template</li>
              <li>Click and drag on the PDF to create the trainer signature zone</li>
              <li>The zone defines where the trainer's signature will appear when they sign</li>
              <li>Select a zone to adjust its label or delete it</li>
              <li>Navigate between pages if your contract spans multiple pages</li>
            </ol>
            <p className="mt-2 font-medium">
              💡 Creator signatures: Use the <code className="bg-blue-200 px-1 rounded">{'{tc_user_signature}'}</code> variable in your template
            </p>
          </div>
        </Card>
      )}

      {/* Warning Card */}
      <Card className="bg-yellow-50 border-yellow-200">
        <div className="text-sm text-yellow-800">
          <h4 className="font-semibold mb-2">⚠️ Important Notes:</h4>
          <ul className="list-disc list-inside space-y-1">
            <li>Signature zones are based on a preview PDF with sample data</li>
            <li>These coordinates apply to ALL contracts generated from this template</li>
            <li>Ensure your template HTML generates consistent PDF layout</li>
            <li>Test with actual contract generation before activating template</li>
            <li>At least one trainer signature zone is required to activate template</li>
          </ul>
        </div>
      </Card>

      {/* Conditionally show PDF viewer for zone-based mode only */}
      {template?.signature_mode === 'variable' ? (
        <Card className="p-8 text-center">
          <div className="max-w-2xl mx-auto">
            <div className="text-6xl mb-4">✅</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Variable-Based Signature Mode Active
            </h3>
            <p className="text-gray-600 mb-4">
              This template uses the <code className="bg-gray-200 px-1 rounded">{'{trainer_signature}'}</code> variable
              for trainer signature placement. No signature zones are required.
            </p>
            <p className="text-sm text-gray-500">
              The trainer's signature will appear wherever you placed the variable in your template HTML,
              flowing naturally with your content regardless of payment schedule length.
            </p>
            <Button
              onClick={() => navigate('/templates')}
              className="mt-6"
            >
              Return to Templates
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* PDF Viewer */}
          <div className="lg:col-span-2">
          <Card>
            {/* Page Navigation */}
            {numPages > 1 && (
              <div className="flex items-center justify-between mb-4 pb-4 border-b">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {numPages}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
                  disabled={currentPage === numPages}
                >
                  Next
                </Button>
              </div>
            )}

            {/* PDF Canvas */}
            {generatingPreview ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Spinner size="lg" />
                <p className="mt-4 text-gray-600">Generating PDF preview...</p>
              </div>
            ) : pdfUrl ? (
              <div
                ref={containerRef}
                className="relative border border-gray-300 rounded-lg overflow-hidden bg-gray-100"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                style={{ cursor: isDragging ? 'crosshair' : 'default' }}
              >
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading={<Spinner />}
                  error={
                    <div className="p-4 text-red-600">
                      Failed to load PDF preview. Check browser console for details.
                    </div>
                  }
                >
                  <Page
                    pageNumber={currentPage}
                    className="pdf-page"
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Document>

                {/* Render signature zones */}
                {zones
                  .filter(zone => zone.page === currentPage)
                  .map(zone => (
                    <div
                      key={zone.id}
                      className={`absolute border-2 border-green-500 bg-green-100 bg-opacity-30 cursor-pointer transition-all ${
                        selectedZone?.id === zone.id ? 'ring-2 ring-offset-2 ring-primary-500' : ''
                      }`}
                      style={{
                        left: `${zone.x}%`,
                        top: `${zone.y}%`,
                        width: `${zone.width}%`,
                        height: `${zone.height}%`,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedZone(zone);
                      }}
                    >
                      <span className="absolute top-0 left-0 text-xs font-semibold px-1 bg-white">
                        {zone.label}
                      </span>
                    </div>
                  ))}

                {/* Render temp zone during drag */}
                {selectedZone?.id === 'temp' && (
                  <div
                    className="absolute border-2 border-dashed border-primary-500 bg-primary-100 bg-opacity-30"
                    style={{
                      left: `${selectedZone.x}%`,
                      top: `${selectedZone.y}%`,
                      width: `${selectedZone.width}%`,
                      height: `${selectedZone.height}%`,
                    }}
                  />
                )}
              </div>
            ) : (
              <div className="p-4 text-gray-600 text-center">
                No PDF preview available. Please ensure your template has HTML content.
              </div>
            )}
          </Card>
        </div>

        {/* Zone List */}
        <div className="space-y-4">
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Trainer Signature Zones ({zones.length})
            </h3>

            {zones.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">
                No zones defined yet. Draw a zone on the PDF to get started.
              </p>
            ) : (
              <div className="space-y-3">
                {zones.map(zone => (
                  <div
                    key={zone.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${
                      selectedZone?.id === zone.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedZone(zone)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <Input
                          value={zone.label}
                          onChange={(e) => handleUpdateZone(zone.id, { label: e.target.value })}
                          className="mb-2"
                          placeholder="Zone label"
                        />
                        <div className="text-xs text-gray-500 space-y-1">
                          <div>Page: {zone.page}</div>
                          <div>Type: Trainer Signature</div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteZone(zone.id);
                        }}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="text-xs text-gray-500 italic">
                      Trainer signature zone
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Zone Info */}
          {selectedZone && selectedZone.id !== 'temp' && (
            <Card className="bg-gray-50">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">
                Selected Zone
              </h4>
              <div className="text-xs text-gray-600 space-y-1">
                <div>Position: ({selectedZone.x.toFixed(1)}%, {selectedZone.y.toFixed(1)}%)</div>
                <div>Size: {selectedZone.width.toFixed(1)}% × {selectedZone.height.toFixed(1)}%</div>
              </div>
            </Card>
          )}
        </div>
        </div>
      )}
    </div>
  );
}